import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  Text, Button, Badge, Tab, TabList, Spinner, Tag,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Input, Dropdown, Option, Field, Textarea,
} from '@fluentui/react-components';
import { ArrowLeftRegular, ClipboardRegular, ErrorCircleRegular, AddRegular, EditRegular } from '@fluentui/react-icons';
import { listRegisters, createRegister, updateRegister, type RegisterEntry, type RegisterType, type FactData, type WorkloadData, type DecisionData, type RiskData, type RaciRowData, type GovernanceForumData, type ChangeRequestData, type UxPolicyDecisionData, type ObligationData } from '../services/register-service';
import { useApiToken } from '../hooks/useApiToken';
import type { Project } from '../services/project-service';
import { getBadgeProps, getRiskColor, FACT_STATUS_BADGE, CR_STATUS_BADGE, UX_DISPOSITION_BADGE, OBLIGATION_STATUS_BADGE } from '../utils/badge-config';

const REGISTER_TABS: { key: RegisterType; labelKey: string }[] = [
  { key: 'fact', labelKey: 'registers.tab.fact' },
  { key: 'workload', labelKey: 'registers.tab.workload' },
  { key: 'decision', labelKey: 'registers.tab.decision' },
  { key: 'risk', labelKey: 'registers.tab.risk' },
  { key: 'obligation', labelKey: 'registers.tab.obligation' },
  { key: 'raci', labelKey: 'registers.tab.raci' },
  { key: 'governance-forum', labelKey: 'registers.tab.governanceForum' },
  { key: 'change-request', labelKey: 'registers.tab.changeRequest' },
  { key: 'ux-policy', labelKey: 'registers.tab.uxPolicy' },
  { key: 'stakeholder', labelKey: 'registers.tab.stakeholder' },
  { key: 'skill', labelKey: 'registers.tab.skill' },
];


const CR_CATEGORY_LABEL_KEY: Record<string, string> = {
  inscope: 'registers.crCategory.inscope',
  minor: 'registers.crCategory.minor',
  material: 'registers.crCategory.material',
};


// Universal RACI column set rendered by the RACI tab. Mirrors the template's column order.
// Sparse cells render as "—". Engagement-specific columns appear for every row but are
// populated only on rows where they apply.
const RACI_COLUMNS: { key: string; labelKey: string }[] = [
  { key: 'customerSponsor', labelKey: 'registers.raci.col.customerSponsor' },
  { key: 'customerItLead', labelKey: 'registers.raci.col.customerItLead' },
  { key: 'customerInfoOfficer', labelKey: 'registers.raci.col.customerInfoOfficer' },
  { key: 'customerAppOwners', labelKey: 'registers.raci.col.customerAppOwners' },
  { key: 'gmsAccountExec', labelKey: 'registers.raci.col.gmsAccountExec' },
  { key: 'gmsProgrammeManager', labelKey: 'registers.raci.col.gmsProgrammeManager' },
  { key: 'gmsArchitect', labelKey: 'registers.raci.col.gmsArchitect' },
  { key: 'gmsServiceDeskLead', labelKey: 'registers.raci.col.gmsServiceDeskLead' },
  { key: 'gmsMigrationLead', labelKey: 'registers.raci.col.gmsMigrationLead' },
  { key: 'gmsDnsEngineer', labelKey: 'registers.raci.col.gmsDnsEngineer' },
  { key: 'gmsSourceEngineer', labelKey: 'registers.raci.col.gmsSourceEngineer' },
  { key: 'gmsAvePointOperator', labelKey: 'registers.raci.col.gmsAvePointOperator' },
  { key: 'gmsCodeTwoEngineer', labelKey: 'registers.raci.col.gmsCodeTwoEngineer' },
];

const ENGAGEMENT_LABEL_KEY: Record<string, string> = {
  programme: 'registers.engagement.programme',
  plan1: 'registers.engagement.plan1',
  mailMigration: 'registers.engagement.mailMigration',
};


interface RegistersPageProps {
  project: Project;
  onBack: () => void;
}

export function RegistersPage({ project, onBack }: RegistersPageProps) {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState<RegisterType>('fact');
  const [entries, setEntries] = useState<RegisterEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getToken = useApiToken();

  const loadEntries = useCallback(async () => {
    if (!project.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const items = await listRegisters(project.customerId, project.id, activeTab, token);
      setEntries(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load registers.');
    } finally {
      setLoading(false);
    }
  }, [project.id, project.customerId, activeTab, getToken]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  return (
    <div className="page-root page-root--scrollable">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'registers.backButton.title' })} />
        <ClipboardRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'registers.title' }, { name: project.name })}</Text>
      </div>

      <div className="audit-navigator-tabs">
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, d) => setActiveTab(d.value as RegisterType)}
          size="small"
          className="mb-md"
        >
          {REGISTER_TABS.map(tab => (
            <Tab key={tab.key} value={tab.key}>{intl.formatMessage({ id: tab.labelKey })}</Tab>
          ))}
        </TabList>

        {loading ? (
          <div className="page-loading"><Spinner size="large" label={intl.formatMessage({ id: 'registers.loading' })} /></div>
        ) : error ? (
          <div className="page-error">
            <ErrorCircleRegular />
            <Text className="text-danger">{error}</Text>
            <Button appearance="primary" onClick={loadEntries}>{intl.formatMessage({ id: 'registers.error.retry' })}</Button>
          </div>
        ) : activeTab === 'obligation' ? (
          <ObligationsSection project={project} entries={entries} onChanged={loadEntries} />
        ) : entries.length === 0 ? (
          <div className="page-empty">
            <ClipboardRegular />
            <Text className="text-muted">{intl.formatMessage({ id: 'registers.empty' }, { tab: activeTab })}</Text>
          </div>
        ) : (
          <div className="table-scroll-container">
            {activeTab === 'fact' && <FactsTable entries={entries} />}
            {activeTab === 'workload' && <WorkloadsTable entries={entries} />}
            {activeTab === 'decision' && <DecisionsTable entries={entries} />}
            {activeTab === 'risk' && <RisksTable entries={entries} />}
            {activeTab === 'raci' && <RaciTable entries={entries} />}
            {activeTab === 'governance-forum' && <GovernanceForumsTable entries={entries} />}
            {activeTab === 'change-request' && <ChangeRequestsTable entries={entries} />}
            {activeTab === 'ux-policy' && <UxPoliciesTable entries={entries} />}
            {activeTab === 'stakeholder' && <GenericTable entries={entries} fields={['name', 'role', 'organisation', 'email', 'raciRole']} />}
            {activeTab === 'skill' && <GenericTable entries={entries} fields={['personName', 'currentRole', 'status']} />}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Obligations register (epic #400) — the human commitments the programme depends on.
 * List + create + edit + a Chase action. The model's rule is enforced here: satisfied
 * requires evidence — never a bare tick — so the dialog blocks status = satisfied until
 * an evidence value is recorded, and stamps satisfiedAt when it is.
 */
function ObligationsSection({ project, entries, onChanged }: {
  project: Project;
  entries: RegisterEntry[];
  onChanged: () => void;
}) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [dialogEntry, setDialogEntry] = useState<RegisterEntry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chasing, setChasing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const openCreate = () => { setDialogEntry(null); setDialogOpen(true); };
  const openEdit = (entry: RegisterEntry) => { setDialogEntry(entry); setDialogOpen(true); };

  const saveObligation = async (data: ObligationData) => {
    const token = await getToken();
    if (!token) return;
    const payload = data as unknown as Record<string, unknown>;
    if (dialogEntry?.id) {
      await updateRegister(dialogEntry.id, { ...dialogEntry, data: payload }, token);
    } else {
      await createRegister(
        { customerId: project.customerId, projectId: project.id!, type: 'obligation', data: payload },
        token,
      );
    }
    setDialogOpen(false);
    onChanged();
  };

  // Chase stamps lastChasedAt = now (and moves an open obligation to chased).
  const chase = async (entry: RegisterEntry) => {
    const d = entry.data as unknown as ObligationData | undefined;
    if (!d || !entry.id || chasing) return;
    setChasing(true);
    setActionError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const data = { ...d, status: 'chased' as const, lastChasedAt: new Date().toISOString() };
      await updateRegister(entry.id, { ...entry, data: data as unknown as Record<string, unknown> }, token);
      onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Chase failed.');
    } finally {
      setChasing(false);
    }
  };

  return (
    <>
      <div className="flex-row mb-md">
        <Button appearance="primary" size="small" icon={<AddRegular />} onClick={openCreate}>
          {intl.formatMessage({ id: 'registers.obligations.btn.new' })}
        </Button>
        {actionError && <Text className="text-danger">{actionError}</Text>}
      </div>
      {entries.length === 0 ? (
        <div className="page-empty">
          <ClipboardRegular />
          <Text className="text-muted">{intl.formatMessage({ id: 'registers.obligations.empty' })}</Text>
        </div>
      ) : (
        <div className="table-scroll-container">
          <table className="data-table obligations-table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.title' })}</th>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.owedBy' })}</th>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.blocks' })}</th>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.due' })}</th>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.status' })}</th>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.lastChased' })}</th>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.evidence' })}</th>
                <th>{intl.formatMessage({ id: 'registers.obligations.col.actions' })}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const d = e.data as unknown as ObligationData | undefined;
                if (!d) return null;
                return (
                  <tr key={e.id}>
                    <td>
                      <strong>{d.title}</strong>
                      {d.detail && <div className="text-muted register-cell-sub">{d.detail}</div>}
                    </td>
                    <td>
                      <div>{d.owedBy}</div>
                      {d.owedByEmail && <div className="text-muted register-cell-sub">{d.owedByEmail}</div>}
                    </td>
                    <td>
                      {(d.blocks ?? []).length === 0 ? '—' : (
                        <div className="tag-chip-row">
                          {(d.blocks ?? []).map(b => (
                            <Tag key={b} size="extra-small" appearance="outline">{b}</Tag>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{d.dueDate ? d.dueDate.split('T')[0] : '—'}</td>
                    <td><Badge size="small" {...getBadgeProps(OBLIGATION_STATUS_BADGE, d.status)}>{d.status}</Badge></td>
                    <td className="text-muted">{d.lastChasedAt ? d.lastChasedAt.split('T')[0] : '—'}</td>
                    <td className="text-muted register-cell-meta">{d.evidence ?? '—'}</td>
                    <td>
                      <div className="flex-row">
                        {(d.status === 'open' || d.status === 'chased') && (
                          <Button size="small" appearance="subtle" disabled={chasing} onClick={() => chase(e)}>
                            {intl.formatMessage({ id: 'registers.obligations.btn.chase' })}
                          </Button>
                        )}
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<EditRegular />}
                          aria-label={intl.formatMessage({ id: 'registers.obligations.btn.edit.ariaLabel' }, { title: d.title })}
                          onClick={() => openEdit(e)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {dialogOpen && (
        <ObligationDialog
          entry={dialogEntry}
          onSave={saveObligation}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}

const OBLIGATION_STATUS_OPTIONS: { value: ObligationData['status']; labelKey: string }[] = [
  { value: 'open', labelKey: 'registers.obligations.status.open' },
  { value: 'chased', labelKey: 'registers.obligations.status.chased' },
  { value: 'satisfied', labelKey: 'registers.obligations.status.satisfied' },
  { value: 'expired', labelKey: 'registers.obligations.status.expired' },
];

function ObligationDialog({ entry, onSave, onClose }: {
  entry: RegisterEntry | null;
  onSave: (data: ObligationData) => Promise<void>;
  onClose: () => void;
}) {
  const intl = useIntl();
  const existing = entry?.data as unknown as ObligationData | undefined;
  const [form, setForm] = useState({
    title: existing?.title ?? '',
    detail: existing?.detail ?? '',
    owedBy: existing?.owedBy ?? '',
    owedByEmail: existing?.owedByEmail ?? '',
    blocks: (existing?.blocks ?? []).join(', '),
    leadTime: existing?.leadTime ?? '',
    failureMode: existing?.failureMode ?? '',
    earlyWarningBinding: existing?.earlyWarningBinding ?? '',
    dueDate: existing?.dueDate ? existing.dueDate.split('T')[0] : '',
    status: (existing?.status ?? 'open') as ObligationData['status'],
    evidence: existing?.evidence ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  // Satisfied requires evidence — never a bare tick (ObligationEntry model rule).
  const evidenceMissing = form.status === 'satisfied' && form.evidence.trim() === '';
  const canSave = form.title.trim() !== '' && form.owedBy.trim() !== '' && !evidenceMissing && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const data: ObligationData = {
        title: form.title.trim(),
        detail: form.detail.trim() || undefined,
        owedBy: form.owedBy.trim(),
        owedByEmail: form.owedByEmail.trim() || undefined,
        blocks: form.blocks.split(',').map(b => b.trim()).filter(Boolean),
        leadTime: form.leadTime.trim() || undefined,
        failureMode: form.failureMode.trim() || undefined,
        earlyWarningBinding: form.earlyWarningBinding.trim() || undefined,
        dueDate: form.dueDate || undefined,
        status: form.status,
        lastChasedAt: existing?.lastChasedAt,
        evidence: form.evidence.trim() || undefined,
        // Satisfied stamps satisfiedAt (kept if already stamped); any other status clears it.
        satisfiedAt: form.status === 'satisfied'
          ? (existing?.satisfiedAt ?? new Date().toISOString())
          : undefined,
      };
      await onSave(data);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(_e, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {intl.formatMessage({ id: entry ? 'registers.obligations.dialog.edit.title' : 'registers.obligations.dialog.create.title' })}
          </DialogTitle>
          <DialogContent>
            <div className="dialog-form">
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.title' })} required>
                <Input value={form.title} onChange={(_e, d) => set('title', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.detail' })}>
                <Textarea value={form.detail} rows={2} onChange={(_e, d) => set('detail', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.owedBy' })} required>
                <Input value={form.owedBy} onChange={(_e, d) => set('owedBy', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.owedByEmail' })}>
                <Input type="email" value={form.owedByEmail} onChange={(_e, d) => set('owedByEmail', d.value)} />
              </Field>
              <Field
                label={intl.formatMessage({ id: 'registers.obligations.field.blocks' })}
                hint={intl.formatMessage({ id: 'registers.obligations.field.blocks.hint' })}
              >
                <Input value={form.blocks} onChange={(_e, d) => set('blocks', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.leadTime' })}>
                <Input value={form.leadTime} onChange={(_e, d) => set('leadTime', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.failureMode' })}>
                <Input value={form.failureMode} onChange={(_e, d) => set('failureMode', d.value)} />
              </Field>
              <Field
                label={intl.formatMessage({ id: 'registers.obligations.field.earlyWarningBinding' })}
                hint={intl.formatMessage({ id: 'registers.obligations.field.earlyWarningBinding.hint' })}
              >
                <Input value={form.earlyWarningBinding} onChange={(_e, d) => set('earlyWarningBinding', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.dueDate' })}>
                <Input type="date" value={form.dueDate} onChange={(_e, d) => set('dueDate', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'registers.obligations.field.status' })}>
                <Dropdown
                  value={intl.formatMessage({ id: `registers.obligations.status.${form.status}` })}
                  selectedOptions={[form.status]}
                  onOptionSelect={(_e, d) => set('status', (d.optionValue ?? 'open') as ObligationData['status'])}
                >
                  {OBLIGATION_STATUS_OPTIONS.map(o => (
                    <Option key={o.value} value={o.value}>{intl.formatMessage({ id: o.labelKey })}</Option>
                  ))}
                </Dropdown>
              </Field>
              <Field
                label={intl.formatMessage({ id: 'registers.obligations.field.evidence' })}
                required={form.status === 'satisfied'}
                validationState={evidenceMissing ? 'error' : 'none'}
                validationMessage={evidenceMissing
                  ? intl.formatMessage({ id: 'registers.obligations.evidence.required' })
                  : undefined}
              >
                <Textarea value={form.evidence} rows={2} onChange={(_e, d) => set('evidence', d.value)} />
              </Field>
              {saveError && <Text className="text-danger">{saveError}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {intl.formatMessage({ id: 'registers.obligations.btn.cancel' })}
            </Button>
            <Button appearance="primary" onClick={handleSave} disabled={!canSave}>
              {saving
                ? intl.formatMessage({ id: 'registers.obligations.btn.saving' })
                : intl.formatMessage({ id: entry ? 'registers.obligations.btn.save' : 'registers.obligations.btn.create' })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function FactsTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.facts.col.statement' })}</th>
          <th>{intl.formatMessage({ id: 'registers.facts.col.category' })}</th>
          <th>{intl.formatMessage({ id: 'registers.facts.col.status' })}</th>
          <th>{intl.formatMessage({ id: 'registers.facts.col.blocking' })}</th>
          <th>{intl.formatMessage({ id: 'registers.facts.col.source' })}</th>
          <th>{intl.formatMessage({ id: 'registers.facts.col.confirmedBy' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as FactData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td>{d.statement}</td>
              <td>{d.category}</td>
              <td><Badge size="small" {...getBadgeProps(FACT_STATUS_BADGE, d.status)}>{d.status}</Badge></td>
              <td>{d.blocking ? <Badge color="danger" size="small">{intl.formatMessage({ id: 'registers.facts.blocking' })}</Badge> : '—'}</td>
              <td className="text-muted register-cell-meta">{d.source}</td>
              <td>{d.confirmedBy ?? '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function WorkloadsTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.workloads.col.name' })}</th>
          <th>{intl.formatMessage({ id: 'registers.workloads.col.type' })}</th>
          <th>{intl.formatMessage({ id: 'registers.workloads.col.target' })}</th>
          <th>{intl.formatMessage({ id: 'registers.workloads.col.strategy' })}</th>
          <th>{intl.formatMessage({ id: 'registers.workloads.col.status' })}</th>
          <th>{intl.formatMessage({ id: 'registers.workloads.col.estimatedCost' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as WorkloadData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td>
                <strong>{d.name}</strong>
                {d.components?.length > 0 && (
                  <div className="text-muted register-cell-sub">{d.components.join(', ')}</div>
                )}
              </td>
              <td>{d.type}</td>
              <td>
                <div>{d.targetAzureSku ?? d.targetService ?? '—'}</div>
                {d.targetSpec && <div className="text-muted register-cell-sub">{d.targetSpec}</div>}
              </td>
              <td>{d.migrationStrategy}</td>
              <td><Badge size="small" appearance="outline">{d.status}</Badge></td>
              <td>{d.estimatedMonthlyCost ? `$${d.estimatedMonthlyCost.toFixed(2)}/mo` : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DecisionsTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.decisions.col.topic' })}</th>
          <th>{intl.formatMessage({ id: 'registers.decisions.col.decision' })}</th>
          <th>{intl.formatMessage({ id: 'registers.decisions.col.rationale' })}</th>
          <th>{intl.formatMessage({ id: 'registers.decisions.col.decidedBy' })}</th>
          <th>{intl.formatMessage({ id: 'registers.decisions.col.date' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as DecisionData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td><strong>{d.topic}</strong></td>
              <td>{d.decision}</td>
              <td className="text-muted register-cell-meta">{d.rationale}</td>
              <td>{d.decider}</td>
              <td>{d.date}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RisksTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.risks.col.description' })}</th>
          <th>{intl.formatMessage({ id: 'registers.risks.col.category' })}</th>
          <th>{intl.formatMessage({ id: 'registers.risks.col.likelihood' })}</th>
          <th>{intl.formatMessage({ id: 'registers.risks.col.impact' })}</th>
          <th>{intl.formatMessage({ id: 'registers.risks.col.score' })}</th>
          <th>{intl.formatMessage({ id: 'registers.risks.col.mitigation' })}</th>
          <th>{intl.formatMessage({ id: 'registers.risks.col.status' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as RiskData | undefined;
          if (!d) return null;
          const score = d.likelihood * d.impact;
          return (
            <tr key={e.id}>
              <td>{d.description}</td>
              <td>{d.category}</td>
              <td>{d.likelihood}</td>
              <td>{d.impact}</td>
              <td><Badge size="small" color={getRiskColor(score)}>{score}</Badge></td>
              <td className="register-cell-meta">{d.mitigation}</td>
              <td><Badge size="small" appearance="outline">{d.status}</Badge></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RaciTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.raci.col.engagement' })}</th>
          <th>{intl.formatMessage({ id: 'registers.raci.col.activity' })}</th>
          {RACI_COLUMNS.map(col => <th key={col.key}>{intl.formatMessage({ id: col.labelKey })}</th>)}
          <th>{intl.formatMessage({ id: 'registers.raci.col.notes' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as RaciRowData | undefined;
          if (!d) return null;
          const engagementLabelKey = ENGAGEMENT_LABEL_KEY[d.engagementContext];
          const engagementLabel = engagementLabelKey
            ? intl.formatMessage({ id: engagementLabelKey })
            : d.engagementContext;
          return (
            <tr key={e.id}>
              <td><Badge size="small" appearance="outline">{engagementLabel}</Badge></td>
              <td>{d.activity}</td>
              {RACI_COLUMNS.map(col => {
                const value = d.responsibilities?.[col.key];
                return <td key={col.key} className="text-center">{value ?? '—'}</td>;
              })}
              <td className="text-muted register-cell-meta">{d.notes ?? '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function GovernanceForumsTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.governance.col.name' })}</th>
          <th>{intl.formatMessage({ id: 'registers.governance.col.frequency' })}</th>
          <th>{intl.formatMessage({ id: 'registers.governance.col.chair' })}</th>
          <th>{intl.formatMessage({ id: 'registers.governance.col.attendees' })}</th>
          <th>{intl.formatMessage({ id: 'registers.governance.col.purpose' })}</th>
          <th>{intl.formatMessage({ id: 'registers.governance.col.output' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as GovernanceForumData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td><strong>{d.name}</strong></td>
              <td>{d.frequency}</td>
              <td>{d.chair}</td>
              <td className="text-muted register-cell-meta">{d.attendees}</td>
              <td className="register-cell-meta">{d.purpose}</td>
              <td className="text-muted register-cell-meta">{d.output ?? '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ChangeRequestsTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.changeRequests.col.title' })}</th>
          <th>{intl.formatMessage({ id: 'registers.changeRequests.col.category' })}</th>
          <th>{intl.formatMessage({ id: 'registers.changeRequests.col.status' })}</th>
          <th>{intl.formatMessage({ id: 'registers.changeRequests.col.approver' })}</th>
          <th>{intl.formatMessage({ id: 'registers.changeRequests.col.raised' })}</th>
          <th>{intl.formatMessage({ id: 'registers.changeRequests.col.impactSummary' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as ChangeRequestData | undefined;
          if (!d) return null;
          const crCategoryKey = CR_CATEGORY_LABEL_KEY[d.category];
          const crCategoryLabel = crCategoryKey
            ? intl.formatMessage({ id: crCategoryKey })
            : d.category;
          return (
            <tr key={e.id}>
              <td><strong>{d.title}</strong></td>
              <td>{crCategoryLabel}</td>
              <td><Badge size="small" {...getBadgeProps(CR_STATUS_BADGE, d.status)}>{d.status}</Badge></td>
              <td>{d.approver || '—'}</td>
              <td>{d.raisedDate ? d.raisedDate.split('T')[0] : '—'}</td>
              <td className="register-cell-meta">{d.impactSummary}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function UxPoliciesTable({ entries }: { entries: RegisterEntry[] }) {
  const intl = useIntl();
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'registers.uxPolicies.col.control' })}</th>
          <th>{intl.formatMessage({ id: 'registers.uxPolicies.col.standard' })}</th>
          <th>{intl.formatMessage({ id: 'registers.uxPolicies.col.area' })}</th>
          <th>{intl.formatMessage({ id: 'registers.uxPolicies.col.disposition' })}</th>
          <th>{intl.formatMessage({ id: 'registers.uxPolicies.col.rationale' })}</th>
          <th>{intl.formatMessage({ id: 'registers.uxPolicies.col.signedBy' })}</th>
          <th>{intl.formatMessage({ id: 'registers.uxPolicies.col.materialChange' })}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as unknown as UxPolicyDecisionData | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              <td>
                <strong>{d.controlId}</strong>
                <div className="text-muted register-cell-sub">{d.controlTitle}</div>
              </td>
              <td>{d.sourceStandard}</td>
              <td>{d.controlArea || '—'}</td>
              <td><Badge size="small" {...getBadgeProps(UX_DISPOSITION_BADGE, d.disposition)}>{d.disposition}</Badge></td>
              <td className="text-muted register-cell-meta">{d.rationale ?? '—'}</td>
              <td>{d.signedBy ?? '—'}</td>
              <td>{d.materialChangeFlag ? <Badge color="warning" size="small">{intl.formatMessage({ id: 'registers.uxPolicies.materialChange.yes' })}</Badge> : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function GenericTable({ entries, fields }: { entries: RegisterEntry[]; fields: string[] }) {
  return (
    <table className="data-table">
      <thead>
        <tr>
          {fields.map(f => <th key={f}>{f.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</th>)}
        </tr>
      </thead>
      <tbody>
        {entries.map(e => {
          const d = e.data as Record<string, unknown> | undefined;
          if (!d) return null;
          return (
            <tr key={e.id}>
              {fields.map(f => <td key={f}>{String(d[f] ?? '—')}</td>)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
